import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import Background from "../../../components/Background";
import Topnav from "../../../components/Topnav";
import CopyRightTextMain from "../../../components/CopyRightTextMain";
import GoalCard from "../../../features/onboarding/components/Goals/GoalCard";
import AviationSectorPicker from "../../../features/onboarding/components/Sectors/AviationSectorPicker";
import OptionPills from "../../../features/onboarding/components/GoalSubflow/OptionPills";
import {
  GOAL_IDS,
  MIN_GOALS,
  getOptions,
  planSubFlows,
} from "../../../features/onboarding/steps/registry";
import { GOAL_ICONS } from "../../../features/onboarding/steps/screens/GoalsStep";
import { fetchGoals, saveGoals } from "../../../features/goals/api";
import { t } from "../../../locales/t";

/**
 * Editing the goals chosen during onboarding.
 *
 * Onboarding asks this over six screens because a member answering for the first
 * time benefits from one question at a time. Editing is the opposite situation:
 * they came here to change one thing and want to see what they already chose, so
 * it is a single page.
 *
 * What it does NOT do is re-implement the questions. `planSubFlows` is the same
 * pure function the onboarding sub-flows use, so this page asks exactly what
 * onboarding asks — including the de-duplication, where selecting a mentorship
 * goal means networking stops asking for sectors. Getting that wrong would let a
 * member save a payload the API rejects.
 */

/** Every question except sectors is a pill list; only experienceStage is single-choice. */
const PILL_QUESTIONS = [
  { field: "careerStage", multiple: true },
  { field: "experienceStage", multiple: false },
  { field: "communityRole", multiple: true },
  { field: "connectionTypes", multiple: true },
  { field: "careerGoals", multiple: true },
  { field: "learningFormats", multiple: true },
];

const emptySectors = () => ({ primary: [], secondary: {} });

/**
 * Copy a goal may not have. `t` echoes the key back on a miss, which is what
 * makes a forgotten label obvious — but in an optional slot it renders the key
 * itself, so an absent one has to become nothing at all.
 */
const optionalCopy = (key) => {
  const copy = t(key);
  return copy === key ? undefined : copy;
};

/** A goal is answerable once every question it is still being asked has an answer. */
const goalIsComplete = (asks, answers = {}) => {
  if (asks.sectors && !(answers.sectors?.primary?.length > 0)) return false;
  if (asks.experienceStage && !answers.experienceStage) return false;
  return true;
};

const EditGoals = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [goalData, setGoalData] = useState({});
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const current = await fetchGoals();
        if (cancelled) return;
        setSelected(current.goals);
        setGoalData(current.goalData);
        setStatus("ready");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The plan is derived, never stored: it has to follow the current selection so
  // that toggling a goal immediately changes what the others ask.
  const plan = useMemo(() => planSubFlows(selected), [selected]);

  const toggle = (goal) => {
    setError("");
    setSelected((current) =>
      current.includes(goal)
        ? current.filter((id) => id !== goal)
        : [...current, goal],
    );
  };

  const setAnswer = (goal, field, value) =>
    setGoalData((current) => ({
      ...current,
      [goal]: { ...(current[goal] ?? {}), [field]: value },
    }));

  // Only the answers still being asked are sent. A question another goal took
  // over is dropped, which is exactly what the API expects.
  const payload = useMemo(
    () => ({
      goals: plan.map(({ goal }) => goal),
      goalData: Object.fromEntries(
        plan.map(({ goal, asks }) => {
          const answers = goalData[goal] ?? {};
          const kept = {};
          if (asks.sectors) kept.sectors = answers.sectors ?? emptySectors();
          for (const { field } of PILL_QUESTIONS) {
            if (asks[field])
              kept[field] =
                answers[field] ?? (field === "experienceStage" ? "" : []);
          }
          return [goal, kept];
        }),
      ),
    }),
    [plan, goalData],
  );

  const incomplete = plan.filter(
    ({ goal, asks }) => !goalIsComplete(asks, payload.goalData[goal]),
  );
  const canSave =
    selected.length >= MIN_GOALS &&
    incomplete.length === 0 &&
    status !== "saving";

  const handleSave = async () => {
    if (!canSave) return;
    setStatus("saving");
    setError("");
    try {
      await saveGoals(payload);
      navigate(-1);
    } catch (e) {
      setStatus("ready");
      setError(e?.message || "Could not save your goals. Please try again.");
    }
  };

  if (status === "loading") {
    return (
      <Background>
        <Topnav />
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      </Background>
    );
  }

  if (status === "error") {
    return (
      <Background>
        <Topnav />
        <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
          <Alert severity="error">
            We could not load your goals. Please refresh and try again.
          </Alert>
        </Box>
      </Background>
    );
  }

  return (
    <Background>
      <Topnav />
      {/* Padding tightens on small screens: this page is where mobile sends its
          members to edit, so the narrow viewport is a primary case, not a
          fallback. */}
      <Box
        data-profile-editor="goals"
        sx={{
          px: { xs: 2, sm: 3, md: 6 },
          py: { xs: 3, md: 4 },
          maxWidth: 720,
          mx: "auto",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Your goals
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
          {t("onboarding.goals.body")}
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 4 }}>
          {GOAL_IDS.map((id) => (
            <GoalCard
              key={id}
              label={t(`onboarding.goals.options.${id}.label`)}
              description={t(`onboarding.goals.options.${id}.description`)}
              icon={GOAL_ICONS[id]}
              selected={selected.includes(id)}
              onToggle={() => toggle(id)}
            />
          ))}
        </Stack>

        {plan.map(({ goal, asks }) => (
          <Box key={goal} sx={{ mb: 5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              {t(`onboarding.goals.options.${goal}.label`)}
            </Typography>

            {asks.sectors && (
              <AviationSectorPicker
                value={goalData[goal]?.sectors ?? emptySectors()}
                onChange={(value) => setAnswer(goal, "sectors", value)}
                mode={asks.sectors}
                label={t(`onboarding.subflow.${goal}.sectorsLabel`)}
                subtitle={optionalCopy(
                  `onboarding.subflow.${goal}.sectorsSubtitle`,
                )}
              />
            )}

            {PILL_QUESTIONS.filter(({ field }) => asks[field]).map(
              ({ field, multiple }) => (
                <OptionPills
                  key={field}
                  options={getOptions(field)}
                  value={goalData[goal]?.[field] ?? (multiple ? [] : "")}
                  onChange={(value) => setAnswer(goal, field, value)}
                  multiple={multiple}
                  label={t(`onboarding.subflow.questions.${field}.label`)}
                />
              ),
            )}
          </Box>
        ))}

        {selected.length < MIN_GOALS && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Choose at least one goal.
          </Alert>
        )}
        {selected.length >= MIN_GOALS && incomplete.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Finish answering{" "}
            {incomplete
              .map(({ goal }) => t(`onboarding.goals.options.${goal}.label`))
              .join(", ")}
            .
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Stacked on mobile so neither control is a thumb-width target. */}
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1.5}
          sx={{ mt: 2 }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{ py: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={!canSave}
            onClick={handleSave}
            sx={{ py: 1.5 }}
          >
            {status === "saving" ? "Saving…" : "Save goals"}
          </Button>
        </Stack>
      </Box>
      <CopyRightTextMain />
    </Background>
  );
};

export default EditGoals;
